import { useCallback, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { MarkdownEditor } from '../MarkdownEditor'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { formatRelativeTime } from '../PageVersionHistory'
import { UserAvatar } from '../UserAvatar'
import { mdiLoading, mdiMessageOutline, mdiPencil, mdiTrashCanOutline } from '@mdi/js'

type IssueCommentsProps = {
    orgCode: string
    issueUid: string
    nbComments: number
}

const COMMENTS_PATH = '/api/v1/organizations/{orgCode}/issues/{issueUid}/comments'
const ISSUE_PATH = '/api/v1/organizations/{orgCode}/issues/{issueUid}'

function initials(name?: string | null, email?: string | null): string {
    const source = name || email || '?'
    const parts = source.trim().split(/\s+/)
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return source.slice(0, 2).toUpperCase()
}

export function IssueComments({ orgCode, issueUid, nbComments }: IssueCommentsProps) {
    const { t } = useTranslation('issues')
    const { user } = useAuth()
    const queryClient = useQueryClient()

    const [draft, setDraft] = useState('')
    // Bumped after each successful post to remount the (initial-value-only)
    // MarkdownEditor composer, clearing it back to empty.
    const [composerKey, setComposerKey] = useState(0)
    const [editingUid, setEditingUid] = useState<string | null>(null)
    const [editDraft, setEditDraft] = useState('')
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

    const { data, isLoading } = $api.useQuery('get', COMMENTS_PATH, {
        params: { path: { orgCode, issueUid } },
    })
    const comments = data?.data ?? []

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const queryKey = query.queryKey as unknown[]
                if (queryKey.length >= 2 && queryKey[0] === 'get') {
                    const path = queryKey[1]
                    if (path === COMMENTS_PATH || path === ISSUE_PATH) {
                        const params = queryKey.find(
                            (item): item is { params?: { path?: Record<string, unknown> } } =>
                                typeof item === 'object' && item !== null && 'params' in item,
                        )?.params
                        return (
                            params?.path?.orgCode === orgCode && params?.path?.issueUid === issueUid
                        )
                    }
                }
                return false
            },
        })
    }, [queryClient, orgCode, issueUid])

    const createMutation = $api.useMutation('post', COMMENTS_PATH, {
        onSuccess: () => {
            setDraft('')
            setComposerKey((k) => k + 1)
            invalidate()
        },
    })

    const updateMutation = $api.useMutation(
        'patch',
        '/api/v1/organizations/{orgCode}/issues/{issueUid}/comments/{commentUid}',
        {
            onSuccess: () => {
                setEditingUid(null)
                invalidate()
            },
        },
    )

    const deleteMutation = $api.useMutation(
        'delete',
        '/api/v1/organizations/{orgCode}/issues/{issueUid}/comments/{commentUid}',
        {
            onSuccess: () => {
                setDeleteTarget(null)
                invalidate()
            },
        },
    )

    const submitComment = useCallback(() => {
        const content = draft.trim()
        if (!content || createMutation.isPending) return
        createMutation.mutate({
            params: { path: { orgCode, issueUid } },
            body: { content },
        })
    }, [draft, createMutation, orgCode, issueUid])

    // Wraps the (contenteditable-based) MarkdownEditor composer: BlockNote
    // doesn't expose a raw onKeyDown prop, so Cmd/Ctrl+Enter-to-submit is
    // caught here via React's normal synthetic bubbling from the editor's
    // root. MarkdownEditor independently stops the *native* propagation of
    // Cmd/Ctrl+Enter so it never reaches document-level listeners.
    const handleComposerKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                submitComment()
            }
        },
        [submitComment],
    )

    const startEdit = useCallback((commentUid: string, content: string) => {
        setEditingUid(commentUid)
        setEditDraft(content)
    }, [])

    const cancelEdit = useCallback(() => {
        setEditingUid(null)
        setEditDraft('')
    }, [])

    const saveEdit = useCallback(
        (commentUid: string) => {
            const content = editDraft.trim()
            if (!content) return
            updateMutation.mutate({
                params: { path: { orgCode, issueUid, commentUid } },
                body: { content },
            })
        },
        [editDraft, updateMutation, orgCode, issueUid],
    )

    // Wraps the MarkdownEditor edit field the same way handleComposerKeyDown
    // wraps the composer: Cmd/Ctrl+Enter-to-save is caught here via React's
    // synthetic bubbling. Escape is handled by MarkdownEditor's own onEscape
    // prop (wired to cancelEdit below), which also stops the *native*
    // propagation so it never reaches the enclosing Sheet.
    const handleEditKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>, commentUid: string) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                saveEdit(commentUid)
            }
        },
        [saveEdit],
    )

    const confirmDelete = useCallback(() => {
        if (!deleteTarget) return
        deleteMutation.mutate({
            params: { path: { orgCode, issueUid, commentUid: deleteTarget } },
        })
    }, [deleteTarget, deleteMutation, orgCode, issueUid])

    return (
        <div className="space-y-4" data-testid="issue-comments">
            <h3 className="text-base font-semibold flex items-center gap-2">
                <AppIcon path={mdiMessageOutline} size="sm" />
                {t('comments.title', { count: nbComments })}
            </h3>

            {isLoading ? (
                <div className="flex items-center justify-center py-4">
                    <AppIcon path={mdiLoading} size="md" className="motion-safe:animate-spin" />
                </div>
            ) : comments.length === 0 ? null : (
                <ul className="space-y-3" data-testid="issue-comments-list">
                    {comments.map((comment) => {
                        const isAuthor = Boolean(user && comment.created_by_uid === user.uid)
                        const isEditing = editingUid === comment.uid
                        return (
                            <li
                                key={comment.uid}
                                className="flex gap-3"
                                data-testid={`issue-comment-${comment.uid}`}
                            >
                                {comment.created_by_uid ? (
                                    <UserAvatar
                                        org={orgCode}
                                        userUid={comment.created_by_uid}
                                        hasPicture={!!comment.created_by?.picture?.document_uid}
                                        name={comment.created_by?.name}
                                        size={32}
                                    />
                                ) : (
                                    <div
                                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground"
                                        aria-hidden="true"
                                    >
                                        {initials(comment.created_by?.name, comment.created_by?.email)}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0 space-y-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium" data-testid="issue-comment-author">
                                            {comment.created_by?.name ||
                                                comment.created_by?.email ||
                                                t('comments.unknown_author')}
                                        </span>
                                        <span
                                            className="text-xs text-muted-foreground"
                                            title={new Date(comment.created_at).toLocaleString()}
                                            data-testid="issue-comment-timestamp"
                                        >
                                            {formatRelativeTime(comment.created_at)}
                                        </span>
                                        {isAuthor && (
                                            <div className="flex items-center gap-1 ml-auto">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    aria-label={t('comments.edit')}
                                                    title={t('comments.edit')}
                                                    onClick={() => startEdit(comment.uid, comment.content)}
                                                    data-testid={`issue-comment-edit-${comment.uid}`}
                                                >
                                                    <AppIcon path={mdiPencil} size="xs" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6 text-destructive hover:text-destructive"
                                                    aria-label={t('comments.delete')}
                                                    title={t('comments.delete')}
                                                    onClick={() => setDeleteTarget(comment.uid)}
                                                    data-testid={`issue-comment-delete-${comment.uid}`}
                                                >
                                                    <AppIcon path={mdiTrashCanOutline} size="xs" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    {isEditing ? (
                                        <div className="space-y-2" onKeyDown={(e) => handleEditKeyDown(e, comment.uid)}>
                                            <MarkdownEditor
                                                value={editDraft}
                                                onChange={setEditDraft}
                                                onEscape={cancelEdit}
                                                minHeight="min-h-[60px]"
                                                autoFocus
                                                data-testid={`issue-comment-edit-textarea-${comment.uid}`}
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => saveEdit(comment.uid)}
                                                    disabled={!editDraft.trim() || updateMutation.isPending}
                                                    data-testid={`issue-comment-save-${comment.uid}`}
                                                >
                                                    {t('comments.save')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={cancelEdit}
                                                    data-testid={`issue-comment-cancel-${comment.uid}`}
                                                >
                                                    {t('comments.cancel')}
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-foreground prose prose-sm dark:prose-invert max-w-none">
                                            <Markdown remarkPlugins={[remarkGfm]}>{comment.content}</Markdown>
                                        </div>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}

            <div className="space-y-2 pt-2 border-t border-border" onKeyDown={handleComposerKeyDown}>
                <MarkdownEditor
                    key={composerKey}
                    value=""
                    onChange={setDraft}
                    placeholder={t('comments.composer_placeholder')}
                    minHeight="min-h-[70px]"
                    data-testid="issue-comment-composer"
                />
                <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{t('comments.composer_hint')}</span>
                    <Button
                        size="sm"
                        onClick={submitComment}
                        disabled={!draft.trim() || createMutation.isPending}
                        data-testid="issue-comment-submit"
                    >
                        {createMutation.isPending ? t('comments.posting') : t('comments.post')}
                    </Button>
                </div>
            </div>

            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('comments.delete_confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('comments.delete_confirm_description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="issue-comment-delete-cancel">
                            {t('comments.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="issue-comment-delete-confirm"
                        >
                            {t('comments.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
